'use client';

// ── Types ─────────────────────────────────────────────────────────────────────

export type TransactionalEmailType =
  | 'approval' |'rejection' |'corroboration' |'rank_change' |'submission_confirmation' |'dispute_escalation';

// ── Internal caller ───────────────────────────────────────────────────────────

async function callSendEmailApi(
  type: TransactionalEmailType,
  recipientUserId: string,
  data: Record<string, any>
): Promise<void> {
  try {
    const res = await fetch('/api/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, recipientUserId, data }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: 'unknown' }));
      console.error(`[transactional-email] API error for ${type}:`, err.error);
    }
  } catch (err: any) {
    console.error(`[transactional-email] fetch error for ${type}:`, err.message);
  }
}

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Send a submission confirmation email after a contribution is submitted.
 */
export async function sendSubmissionConfirmationEmail(params: {
  recipientUserId: string;
  contributionTitle: string;
  contributionId: string;
}): Promise<void> {
  await callSendEmailApi('submission_confirmation', params.recipientUserId, {
    contributionTitle: params.contributionTitle,
    contributionId: params.contributionId,
  });
}

/**
 * Send a contribution approval email if the user has opted in.
 */
export async function sendApprovalEmail(params: {
  recipientUserId: string;
  contributionTitle: string;
  contributionId: string;
}): Promise<void> {
  await callSendEmailApi('approval', params.recipientUserId, {
    contributionTitle: params.contributionTitle,
    contributionId: params.contributionId,
  });
}

/**
 * Send a contribution rejection email if the user has opted in.
 */
export async function sendRejectionEmail(params: {
  recipientUserId: string;
  contributionTitle: string;
  contributionId: string;
  reason?: string;
}): Promise<void> {
  await callSendEmailApi('rejection', params.recipientUserId, {
    contributionTitle: params.contributionTitle,
    contributionId: params.contributionId,
    reason: params.reason,
  });
}

/**
 * Send a corroboration received email if the contribution author has opted in.
 */
export async function sendCorroborationEmail(params: {
  recipientUserId: string;
  contributionTitle: string;
  contributionId: string;
  corroboratorName?: string;
}): Promise<void> {
  await callSendEmailApi('corroboration', params.recipientUserId, {
    contributionTitle: params.contributionTitle,
    contributionId: params.contributionId,
    corroboratorName: params.corroboratorName ?? 'A community member',
  });
}

/**
 * Send a rank change email if the user has opted in.
 */
export async function sendRankChangeEmail(params: {
  recipientUserId: string;
  oldRank: string;
  newRank: string;
  reputationScore: number;
}): Promise<void> {
  const promoted =
    ['new', 'trusted', 'historian', 'elder'].indexOf(params.newRank) >
    ['new', 'trusted', 'historian', 'elder'].indexOf(params.oldRank);
  await callSendEmailApi('rank_change', params.recipientUserId, {
    oldRank: params.oldRank,
    newRank: params.newRank,
    reputationScore: params.reputationScore,
    promoted,
  });
}

/**
 * Send a dispute escalation email to the contribution author.
 */
export async function sendDisputeEscalationEmail(params: {
  recipientUserId: string;
  contributionTitle: string;
  contributionId: string;
  disputeType: string;
  disputerName?: string;
  claim?: string;
}): Promise<void> {
  await callSendEmailApi('dispute_escalation', params.recipientUserId, {
    contributionTitle: params.contributionTitle,
    contributionId: params.contributionId,
    disputeType: params.disputeType,
    disputerName: params.disputerName ?? 'A community member',
    claim: params.claim ?? '',
  });
}
